import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useLang } from "@/lib/LangContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, CheckCircle2, Clock, Wrench, PackageCheck, XCircle } from "lucide-react";

const STATUS_ICONS = {
  pending: Clock,
  confirmed: CheckCircle2,
  in_progress: Wrench,
  completed: PackageCheck,
  cancelled: XCircle,
};

const STATUS_LABEL = {
  es: {
    pending: "Recibida",
    confirmed: "Confirmada",
    in_progress: "En reparación",
    completed: "Lista para entrega",
    cancelled: "Cancelada",
  },
  en: {
    pending: "Received",
    confirmed: "Confirmed",
    in_progress: "In repair",
    completed: "Ready for pickup",
    cancelled: "Cancelled",
  },
};

const STAGES = ["pending", "confirmed", "in_progress", "completed"];

export default function Tracking() {
  const { lang, t } = useLang();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(paramId || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [appt, setAppt] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!ref || !phone) {
      toast.error(lang === "es" ? "Referencia y teléfono son requeridos" : "Reference and phone required");
      return;
    }
    setLoading(true);
    setAppt(null);
    try {
      const r = await http.post("/track", { reference: ref.trim(), phone: phone.trim() });
      setAppt(r.data);
    } catch (err) {
      toast.error(
        err.response?.status === 404
          ? lang === "es"
            ? "No encontramos esa cita. Verifica la referencia y el teléfono."
            : "We couldn't find that appointment. Check the reference and phone."
          : lang === "es"
          ? "Error al buscar"
          : "Lookup failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header active="track" />
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
        <div className="tick-label" data-testid="track-eyebrow">
          {lang === "es" ? "Seguimiento" : "Tracking"}
        </div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] mt-6">
          {lang === "es" ? "Rastrea tu reparación" : "Track your repair"}
        </h1>
        <p className="text-neutral-600 mt-4 max-w-xl">
          {lang === "es"
            ? "Ingresa tu referencia (ej. appt_xxxxx) y el teléfono con el que agendaste."
            : "Enter your reference (e.g. appt_xxxxx) and the phone you booked with."}
        </p>

        <form
          onSubmit={lookup}
          className="mt-10 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 max-w-3xl"
          data-testid="track-form"
        >
          <Input
            data-testid="track-ref"
            className="rounded-none"
            placeholder={lang === "es" ? "Referencia" : "Reference"}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
          />
          <Input
            data-testid="track-phone"
            className="rounded-none"
            placeholder={lang === "es" ? "Teléfono" : "Phone"}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Button
            type="submit"
            data-testid="track-submit"
            disabled={loading}
            className="rounded-none bg-[#6B1F27] hover:bg-[#4A1519] text-white h-10"
          >
            <Search size={16} className="mr-2" />
            {loading ? "…" : lang === "es" ? "Buscar" : "Search"}
          </Button>
        </form>

        {appt && (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="track-result">
            <div className="lg:col-span-7 hairline bg-white p-8">
              <div className="tick-label">{lang === "es" ? "Estado actual" : "Current status"}</div>
              <div className="mt-4 flex items-center gap-3">
                {(() => {
                  const Icon = STATUS_ICONS[appt.status] || Clock;
                  return <Icon size={28} className="text-[#6B1F27]" />;
                })()}
                <div className="font-display text-3xl md:text-4xl tracking-tighter">
                  {STATUS_LABEL[lang][appt.status]}
                </div>
              </div>

              {appt.status !== "cancelled" && (
                <div className="mt-10">
                  <div className="grid grid-cols-4 gap-2">
                    {STAGES.map((s, i) => {
                      const activeIdx = STAGES.indexOf(appt.status);
                      const done = i <= activeIdx;
                      return (
                        <div key={s} className="flex flex-col items-start gap-2">
                          <div className={`h-1 w-full ${done ? "bg-[#6B1F27]" : "bg-neutral-200"}`} />
                          <div className={`tick-label ${done ? "!text-[#6B1F27]" : ""}`}>
                            {STATUS_LABEL[lang][s]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {appt.technician && (
                <div className="mt-8 tick-label">
                  {lang === "es" ? "Técnico" : "Technician"}: <span className="font-mono-tight text-black">{appt.technician}</span>
                </div>
              )}
            </div>

            <aside className="lg:col-span-5 hairline bg-white p-8 text-sm">
              <div className="tick-label mb-4">{lang === "es" ? "Detalles" : "Details"}</div>
              <dl className="divide-y divide-neutral-200">
                <Row label={t.booking.device_type} value={`${appt.device_type} — ${appt.device_brand} ${appt.device_model}`} />
                <Row label={t.booking.service_type} value={appt.service_type === "at_home" ? t.booking.at_home : t.booking.in_shop} />
                <Row label={t.booking.date} value={`${appt.appointment_date} · ${appt.appointment_time}`} />
                <Row label={lang === "es" ? "Referencia" : "Reference"} value={appt.id} />
              </dl>
              <button
                onClick={() => navigate("/book")}
                className="btn-ghost mt-6 w-full justify-center"
                data-testid="track-book-another"
              >
                {lang === "es" ? "Agendar otra reparación" : "Book another repair"}
              </button>
            </aside>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-3 flex items-start justify-between gap-6">
      <dt className="tick-label whitespace-nowrap">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
